/**
 * "Report a problem": the one form, for every way in (`openReportProblem`). A
 * drawer rather than a dropdown, so it can hold a guided report, and it is the
 * person's words at stake, so a stray press outside never closes it.
 *
 * What it sends is the reporter's answers. Everything about the moment is taken
 * by the server when the report arrives (`api/tickets.py::_capture_snapshot`),
 * so it can be neither forged nor forgotten.
 */
import { useEffect, useState } from 'react';
import { useLifts } from '../../api/hooks/useLifts';
import { useDoors } from '../../api/hooks/useDoors';
import { useRobots } from '../../api/hooks';
import { useCreateSupportTicket } from '../../api/hooks/useTickets';
import { robotReceiptKey } from '../../api/robot-receipts';
import { useCanSeeSimulated } from '../../auth/useCanSeeSimulated';
import { useAuth } from '../../auth/useAuth';
import { audienceFor } from '../../auth/roles';
import { getOptionalRuntimeConfig } from '../../config/runtime-config';
import { useRobotPresence } from '../../screens/Map/useRobotPresence';
import { locationText } from '../../screens/Fleet/robot-lines';
import { useMinuteTick } from '../../state/clock';
import { isSimulatedFleet } from '../../state/simulated-fleet';
import { REPORT_PROBLEM_EVENT, type ReportCategory, type ReportPrefill } from '../../state/report-problem';
import { useActiveSite } from '../../state/useActiveSite';
import { resolveSiteTimeZone } from '../../screens/Tasks/site-timezone';
import {
  NOT_SURE,
  QUICK_PICKS,
  REPORT_CATEGORIES,
  attachmentLines,
  canSend,
  ticketFrom,
} from './report-form';
import './ReportProblemHost.css';

const TEXT_MAX = 2000;

export function ReportProblemHost() {
  // A new key per opening: a fresh form, with what this way in knows.
  const [opening, setOpening] = useState<{ key: number; prefill: ReportPrefill } | null>(null);
  useEffect(() => {
    const onOpen = (e: Event) => {
      const prefill = (e as CustomEvent<ReportPrefill>).detail ?? {};
      setOpening((cur) => ({ key: (cur?.key ?? 0) + 1, prefill }));
    };
    window.addEventListener(REPORT_PROBLEM_EVENT, onOpen);
    return () => window.removeEventListener(REPORT_PROBLEM_EVENT, onOpen);
  }, []);
  if (!opening) return null;
  return (
    <ReportDrawer key={opening.key} prefill={opening.prefill} onClose={() => setOpening(null)} />
  );
}

function ReportDrawer({ prefill, onClose }: { prefill: ReportPrefill; onClose: () => void }) {
  const [category, setCategory] = useState<ReportCategory | null>(prefill.category ?? null);
  const [subject, setSubject] = useState<string | null>(prefill.subject ?? null);
  const [quickPick, setQuickPick] = useState<string | null>(null);
  const [text, setText] = useState(prefill.note ?? '');
  const [sent, setSent] = useState<string | null>(null);
  const create = useCreateSupportTicket();
  const { roles } = useAuth();
  const audience = audienceFor(roles);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Only with nothing to lose: Esc must not eat a half-written report.
      if (e.key === 'Escape' && !text.trim()) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, text]);

  const phone = getOptionalRuntimeConfig()?.supportPhone;
  const send = () => {
    if (!category) return;
    create.mutate(
      ticketFrom({
        category,
        subject,
        quickPick,
        text,
        issueId: prefill.issueId,
        alertId: prefill.alertId,
      }),
      { onSuccess: (ticket) => setSent(ticket.ticket_id) },
    );
  };

  return (
    <aside className="nx-report" role="dialog" aria-label="Report a problem" data-testid="report-drawer">
      <header className="nx-report__head">
        <h2>Report a problem</h2>
        <button type="button" onClick={onClose} aria-label="Close" data-testid="report-close">
          ×
        </button>
      </header>
      {sent ? (
        <div className="nx-report__body" data-testid="report-sent">
          <p className="nx-report__sent">Sent. Nexsoss has it.</p>
          <p className="nx-report__small">
            Your reference is <code>{sent}</code>. Quote it if you call.
          </p>
          <button type="button" className="nx-screen-btn nx-screen-btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      ) : (
        <>
          <div className="nx-report__body">
            <fieldset className="nx-report__group">
              <legend>What is it about?</legend>
              <div className="nx-report__chips">
                {REPORT_CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="nx-report__chip"
                    aria-pressed={category === c.id}
                    onClick={() => {
                      setCategory(c.id);
                      setSubject(null);
                      setQuickPick(null);
                    }}
                    data-testid={`report-category-${c.id}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </fieldset>

            {category === 'robot' && <WhichRobot chosen={subject} onChoose={setSubject} />}
            {category === 'lift_door' && <WhichLiftOrDoor chosen={subject} onChoose={setSubject} />}

            {category && QUICK_PICKS[category].length > 0 && (
              <fieldset className="nx-report__group">
                <legend>What happened?</legend>
                <div className="nx-report__chips">
                  {QUICK_PICKS[category].map((pick) => (
                    <button
                      key={pick}
                      type="button"
                      className="nx-report__chip"
                      aria-pressed={quickPick === pick}
                      onClick={() => setQuickPick(pick)}
                    >
                      {pick}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            <label className="nx-report__group">
              <span className="nx-report__legend">Tell us more</span>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, TEXT_MAX))}
                rows={4}
                maxLength={TEXT_MAX}
                placeholder="What did you see, and where?"
                data-testid="report-text"
              />
            </label>

            <div className="nx-report__attach" data-testid="report-attach">
              <strong>We’ll attach</strong>
              <ul>
                {attachmentLines(category === 'robot' && subject !== NOT_SURE ? subject : null).map(
                  (line) => (
                    <li key={line}>{line}</li>
                  ),
                )}
              </ul>
            </div>
            {create.isError && (
              <p className="nx-report__error" role="alert" data-testid="report-error">
                {audience === 'ops' && create.error instanceof Error
                  ? create.error.message
                  : 'It didn’t send. Your words are still here: try again.'}
              </p>
            )}
          </div>
          <footer className="nx-report__foot">
            {phone && <p className="nx-report__small">Urgent? Call {phone}</p>}
            <div className="nx-report__buttons">
              <button type="button" className="nx-screen-btn" onClick={onClose} data-testid="report-cancel">
                Cancel
              </button>
              <button
                type="button"
                className="nx-screen-btn nx-screen-btn--primary"
                disabled={!category || !canSend(text, quickPick) || create.isPending}
                onClick={send}
                data-testid="report-send"
              >
                {create.isPending ? 'Sending…' : 'Send to Nexsoss'}
              </button>
            </div>
          </footer>
        </>
      )}
    </aside>
  );
}

function WhichRobot({
  chosen,
  onChoose,
}: {
  chosen: string | null;
  onChoose: (name: string) => void;
}) {
  const showSimulated = useCanSeeSimulated();
  const robots = (useRobots().data ?? []).filter((r) => showSimulated || !isSimulatedFleet(r.fleet));
  const lifts = useLifts().data;
  const presence = useRobotPresence(robots, lifts);
  const { site, sites } = useActiveSite();
  const zone = resolveSiteTimeZone(sites, site?.id);
  const wallNowMs = useMinuteTick();
  const picked = robots.find((r) => r.name === chosen);
  const fact = picked
    ? locationText(
        presence.get(robotReceiptKey(picked.fleet, picked.name)),
        zone.source === 'site' ? zone.timeZone : null,
        wallNowMs,
      )
    : null;
  return (
    <fieldset className="nx-report__group">
      <legend>Which robot?</legend>
      <div className="nx-report__chips">
        {[...robots.map((r) => r.name), NOT_SURE].map((name) => (
          <button
            key={name}
            type="button"
            className="nx-report__chip"
            aria-pressed={chosen === name}
            onClick={() => onChoose(name)}
            data-testid={`report-robot-${name}`}
          >
            {name}
          </button>
        ))}
      </div>
      {fact && (
        <p className="nx-report__small" data-testid="report-robot-fact">
          {fact}
        </p>
      )}
    </fieldset>
  );
}

function WhichLiftOrDoor({
  chosen,
  onChoose,
}: {
  chosen: string | null;
  onChoose: (name: string) => void;
}) {
  const lifts = useLifts().data ?? [];
  const doors = useDoors().data ?? [];
  return (
    <label className="nx-report__group">
      <span className="nx-report__legend">Which one?</span>
      <select
        value={chosen ?? ''}
        onChange={(e) => onChoose(e.target.value)}
        data-testid="report-device"
      >
        <option value="">Choose…</option>
        <option value={NOT_SURE}>{NOT_SURE}</option>
        {lifts.length > 0 && (
          <optgroup label="Lifts">
            {lifts.map((l) => (
              <option key={l.name} value={l.name}>
                {l.name}
              </option>
            ))}
          </optgroup>
        )}
        {doors.length > 0 && (
          <optgroup label="Doors">
            {doors.map((d) => (
              <option key={d.name} value={d.name}>
                {d.name}
              </option>
            ))}
          </optgroup>
        )}
      </select>
    </label>
  );
}
